import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Outlet, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/lib/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { format } from 'date-fns';
import { useDropzone } from 'react-dropzone';
import './App.css';

// 设置 dayjs 中文
dayjs.locale('zh-cn');
import WorkorderDashboard from './WorkorderDashboard';
import BidDocumentBuilder from './BidDocumentBuilder';
import HomePage from './HomePage';
import PortalHome from './PortalHome';
import ISMExtractor from './ISMExtractor';
import KnowledgeManager from './KnowledgeManager';
import KnowledgeBrowser from './KnowledgeBrowser';
import KnowledgeSearch from './KnowledgeSearch';
import KnowledgeQA from './KnowledgeQA';
import DataDictionary from './DataDictionary';
import LogAnalysis from './LogAnalysis';
import ProfessionalAssistant from './ProfessionalAssistant';
import QAStats from './QAStats';
import Register from './Register';
import Profile from './Profile';
import PatchManager from './PatchManager';
import WorkorderImport from './WorkorderImport';
import Handover from './Handover';
import ManualsPage from './ManualsPage';
import ProjectList from './ProjectList';
import ProjectDetail from './ProjectDetail';
import PermissionManager from './PermissionManager';
import UserManager from './UserManager';
import AnnouncementManager from './AnnouncementManager';
import TaskMaintenance from './TaskMaintenance';
import TaskBoard from './TaskBoard';
import CompletedTaskMaintenance from './CompletedTaskMaintenance';
import TaskOverview from './TaskOverview';
import TaskCreate from './TaskCreate';
import MonitorDashboard from './MonitorDashboard';
import MonitorConfig from './MonitorConfig';
import MonitorDataEntry from './MonitorDataEntry';
import MonitorDataViewer from './MonitorDataViewer';
import DiagramList from './DiagramList';
import DiagramEditor from './DiagramEditor';
import MonitorReport from './MonitorReport';
import MonitorLayoutConfig from './MonitorLayoutConfig';
import LawDashboard from './LawDashboard';
import CaseManager from './CaseManager';
import WorkLogManager from './WorkLogManager';
import LegalReport from './LegalReport';
import LegalQA from './LegalQA';
import WorkGroupManager from './WorkGroupManager';
import WorkLogEntry from './WorkLogEntry';
import WorkLogHome from './WorkLogHome';
import WorkLogOverview from './WorkLogOverview';
import WorkLogReport from './WorkLogReport';
import WorkLogImport from './WorkLogImport';
import WorkLogAttachments from './WorkLogAttachments';
import ProjectDocHome from './ProjectDocHome';
import ProjectDocDetail from './ProjectDocDetail';
import ProjectDocOverview from './ProjectDocOverview';
import NotificationBell from './NotificationBell';
import NotificationDot from './NotificationDot';
import TopNavbar from './TopNavbar';

// API URL 配置
const API_URL = process.env.REACT_APP_API_URL || '';

// ===== 获取当前用户 =====
const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

// fetch with auth token
const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'x-token': token
    }
  });
  // 处理session过期
  if (res.status === 401) {
    const data = await res.json().catch(() => ({}));
    if (data.error === '登录超时，请重新登录') {
      localStorage.removeItem('notes_token');
      localStorage.removeItem('notes_user');
      localStorage.removeItem('notes_realname');
      alert('登录超时，请重新登录');
      window.location.href = '/';
    } else {
      localStorage.removeItem('notes_token');
      localStorage.removeItem('notes_user');
      localStorage.removeItem('notes_realname');
      alert('登录超时，请重新登录');
      window.location.href = '/';
    }
  }
  return res;
};

// ===== 模板定义 =====
const TEMPLATES = {
  meeting: {
    name: '📋 会议纪要',
    getContent: () => ({
      type: 'meeting',
      title: '',
      sections: [
        { type: 'header', label: '会议主题', value: '' },
        { type: 'header', label: '会议时间', value: '' },
        { type: 'header', label: '会议地点', value: '' },
        { type: 'header', label: '主持人', value: '' },
        { type: 'header', label: '参会人员', value: '' },
        { type: 'text', label: '一、会议议题', value: '' },
        { type: 'text', label: '二、会议内容', value: '' },
        { 
          type: 'table', 
          label: '三、决策事项', 
          columns: ['序号', '事项', '负责人', '完成时间'],
          columnWidths: ['28px', '1fr', '120px', '120px'],
          rows: [{ cols: ['1', '', '', ''] }]
        },
        { 
          type: 'table', 
          label: '四、待办事项', 
          columns: ['序号', '待办事项', '负责人', '完成时间'],
          columnWidths: ['28px', '1fr', '120px', '120px'],
          rows: [{ cols: ['1', '', '', ''] }]
        },
        { type: 'header', label: '五、下次会议安排', value: '' },
        { type: 'header', label: '时间', value: '' },
        { type: 'header', label: '地点', value: '' },
      ]
    })
  },
  research: {
    name: '📊 需求调研',
    getContent: () => ({
      type: 'research',
      title: '',
      sections: [
        { type: 'header', label: '调研主题', value: '' },
        { 
          type: 'table', 
          label: '需求明细表', 
          columns: ['序号', '业务模块', '需求描述', '提出人', '现场答复'],
          columnWidths: ['28px', '12%', '40%', '8%', '35%'],
          rows: [{ cols: ['1', '', '', '', ''] }, { cols: ['2', '', '', '', ''] }, { cols: ['3', '', '', '', ''] }]
        },
        { type: 'text', label: '补充说明', value: '' },
        { type: 'text', label: '调研结论', value: '' },
        { type: 'header', label: '调研人', value: '' },
        { type: 'header', label: '调研时间', value: '' },
      ]
    })
  },
  daily: {
    name: '📝 日常记录',
    getContent: () => ({
      type: 'daily',
      content: ''
    })
  },
  whiteboard: {
    name: '🎨 白板',
    getContent: () => ({
      type: 'whiteboard',
      elements: [],
      appState: { viewBackgroundColor: '#ffffff' }
    })
  }
};

// ===== 轻量白板编辑器组件（Canvas 自绘，零依赖） =====
function WhiteboardEditor({ elements, appState, onChange, title, onTitleChange }) {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pen');           // pen | line | rect | circle | text
  const [color, setColor] = useState('#333333');
  const [lineWidth, setLineWidth] = useState(2);
  const [drawing, setDrawing] = useState(false);
  const currentStroke = useRef([]);
  const dragStart = useRef(null);
  const dragEnd = useRef(null);
  const [elementsState, setElementsState] = useState([]);
  const [textInput, setTextInput] = useState({ show: false, x: 0, y: 0, value: '' });
  const saveTimerRef = useRef(null);
  const containerRef = useRef(null);

  // 固定虚分辨率（导出+存储统一用这个尺寸）
  const VIRTUAL_W = 1200;
  const VIRTUAL_H = 800;

  // 初始化元素
  useEffect(() => {
    if (elements && elements.length > 0) {
      setElementsState(elements);
    }
  }, []);

  // 渲染所有元素（用虚分辨率）
  const renderAll = useCallback((ctx, currentElements) => {
    ctx.clearRect(0, 0, VIRTUAL_W, VIRTUAL_H);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, VIRTUAL_W, VIRTUAL_H);

    (currentElements || elementsState).forEach(el => {
      ctx.strokeStyle = el.color || '#333';
      ctx.lineWidth = el.lineWidth || 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.fillStyle = el.color || '#333';

      if (el.type === 'stroke' && el.points) {
        ctx.beginPath();
        el.points.forEach((p, i) => {
          i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();
      } else if (el.type === 'line') {
        ctx.beginPath();
        ctx.moveTo(el.start.x, el.start.y);
        ctx.lineTo(el.end.x, el.end.y);
        ctx.stroke();
      } else if (el.type === 'rect') {
        const x = Math.min(el.start.x, el.end.x);
        const y = Math.min(el.start.y, el.end.y);
        const w = Math.abs(el.end.x - el.start.x);
        const h = Math.abs(el.end.y - el.start.y);
        if (el.fill && el.fill !== 'transparent') {
          ctx.fillStyle = el.fill;
          ctx.fillRect(x, y, w, h);
          ctx.strokeRect(x, y, w, h);
        } else {
          ctx.strokeRect(x, y, w, h);
        }
      } else if (el.type === 'circle') {
        const cx = (el.start.x + el.end.x) / 2;
        const cy = (el.start.y + el.end.y) / 2;
        const rx = Math.abs(el.end.x - el.start.x) / 2;
        const ry = Math.abs(el.end.y - el.start.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        if (el.fill && el.fill !== 'transparent') {
          ctx.fillStyle = el.fill;
          ctx.fill();
        }
        ctx.stroke();
      } else if (el.type === 'text') {
        ctx.font = `${el.fontSize || 20}px sans-serif`;
        ctx.fillText(el.text, el.x, el.y);
      }
    });
  }, [elementsState]);

  // 自动保存
  const triggerSave = useCallback((newElements) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onChange(newElements, { viewBackgroundColor: '#ffffff' });
    }, 500);
  }, [onChange]);

  // 从CSS坐标映射到虚分辨率坐标
  const cssToCanvas = (cssX, cssY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: cssX * (VIRTUAL_W / rect.width),
      y: cssY * (VIRTUAL_H / rect.height)
    };
  };

  // 鼠标/触屏事件
  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    const cssX = (touch?.clientX || e.clientX) - rect.left;
    const cssY = (touch?.clientY || e.clientY) - rect.top;
    return cssToCanvas(cssX, cssY);
  };

  const handlePointerDown = (e) => {
    const pos = getCanvasPos(e);
    if (tool === 'text') {
      setTextInput({ ...textInput, show: true, x: pos.x, y: pos.y, value: '' });
      return;
    }
    setDrawing(true);
    if (tool === 'pen') {
      currentStroke.current = [pos];
    } else {
      dragStart.current = pos;
      dragEnd.current = pos;
    }
  };

  const handlePointerMove = (e) => {
    if (!drawing) return;
    const pos = getCanvasPos(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (tool === 'pen') {
      currentStroke.current.push(pos);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      renderAll(ctx, elementsState);
      const pts = currentStroke.current;
      pts.forEach((p, i) => {
        i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
    } else {
      dragEnd.current = pos;
      renderAll(ctx, elementsState);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      drawShape(ctx, tool, dragStart.current, dragEnd.current);
    }
  };

  const handlePointerUp = (e) => {
    if (!drawing) return;
    setDrawing(false);
    const pos = getCanvasPos(e);

    let newElements;
    if (tool === 'pen') {
      if (currentStroke.current.length < 2) {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) renderAll(ctx, elementsState);
        return;
      }
      newElements = [...elementsState, { type: 'stroke', points: [...currentStroke.current], color, lineWidth: lineWidth * 2 }];
      currentStroke.current = [];
    } else {
      const end = pos;
      newElements = [...elementsState, { type: tool, start: { ...dragStart.current }, end: { ...end }, color, lineWidth, fill: 'transparent' }];
      dragStart.current = null;
      dragEnd.current = null;
    }
    setElementsState(newElements);
    triggerSave(newElements);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) renderAll(ctx, newElements);
  };

  const drawShape = (ctx, type, start, end) => {
    const s = start || { x: 0, y: 0 };
    const e = end || { x: 0, y: 0 };
    ctx.beginPath();
    if (type === 'line') {
      ctx.moveTo(s.x, s.y); ctx.lineTo(e.x, e.y);
    } else if (type === 'rect') {
      ctx.rect(Math.min(s.x, e.x), Math.min(s.y, e.y), Math.abs(e.x - s.x), Math.abs(e.y - s.y));
    } else if (type === 'circle') {
      const cx = (s.x + e.x) / 2, cy = (s.y + e.y) / 2;
      ctx.ellipse(cx, cy, Math.abs(e.x - s.x) / 2, Math.abs(e.y - s.y) / 2, 0, 0, Math.PI * 2);
    }
    ctx.stroke();
  };

  // 撤销
  const undo = () => {
    if (elementsState.length === 0) return;
    const newElements = elementsState.slice(0, -1);
    setElementsState(newElements);
    triggerSave(newElements);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) renderAll(ctx, newElements);
  };

  // 清空
  const clearAll = () => {
    if (elementsState.length === 0) return;
    if (!confirm('确定清空白板？')) return;
    setElementsState([]);
    triggerSave([]);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) renderAll(ctx, []);
  };

  // 画布初始化（固定虚分辨率）
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = VIRTUAL_W;
    canvas.height = VIRTUAL_H;
    const ctx = canvas.getContext('2d');
    renderAll(ctx, elementsState);
  }, [renderAll]);

  // 提交文字
  const submitText = () => {
    if (!textInput.value.trim()) {
      setTextInput({ show: false, x: 0, y: 0, value: '' });
      return;
    }
    const newEl = { type: 'text', x: textInput.x, y: textInput.y, text: textInput.value, color, fontSize: 20 };
    const newElements = [...elementsState, newEl];
    setElementsState(newElements);
    triggerSave(newElements);
    setTextInput({ show: false, x: 0, y: 0, value: '' });
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) renderAll(ctx, newElements);
  };

  const COLORS = ['#333333', '#e74c3c', '#2ecc71', '#3498db', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22'];
  const TOOLS = [
    { key: 'pen', label: '✏️' },
    { key: 'line', label: '📏' },
    { key: 'rect', label: '▭' },
    { key: 'circle', label: '○' },
    { key: 'text', label: 'T' },
  ];

  // 每次 elementsState 变化后重绘（外部恢复数据时）
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) renderAll(ctx, elementsState);
  }, [elementsState, renderAll]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 工具栏 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', background: '#fff',
        borderBottom: '1px solid #e8e8e8', flexShrink: 0,
        flexWrap: 'wrap'
      }}>
        {/* 标题输入 */}
        <input
          value={title || ''}
          onChange={e => onTitleChange && onTitleChange(e.target.value)}
          placeholder="输入白板标题..."
          style={{
            padding: '5px 10px', border: '1px solid #d9d9d9',
            borderRadius: 4, fontSize: 14, minWidth: 180, outline: 'none'
          }}
        />

        <div style={{ width: 1, height: 24, background: '#e8e8e8', margin: '0 4px' }} />

        {TOOLS.map(t => (
          <button key={t.key}
            onClick={() => setTool(t.key)}
            style={{
              padding: '5px 10px', border: tool === t.key ? '2px solid #1890ff' : '1px solid #d9d9d9',
              borderRadius: 4, background: tool === t.key ? '#e6f7ff' : '#fff',
              cursor: 'pointer', fontSize: 14, fontWeight: t.key === 'text' ? 700 : 'normal'
            }}>
            {t.label}
          </button>
        ))}

        <div style={{ width: 1, height: 24, background: '#e8e8e8', margin: '0 4px' }} />

        {COLORS.map(c => (
          <div key={c}
            onClick={() => setColor(c)}
            style={{
              width: 22, height: 22, borderRadius: '50%', background: c,
              cursor: 'pointer', border: color === c ? '3px solid #1890ff' : '2px solid #ddd',
              boxSizing: 'border-box'
            }}
          />
        ))}

        <div style={{ width: 1, height: 24, background: '#e8e8e8', margin: '0 4px' }} />

        <span style={{ fontSize: 12, color: '#999' }}>粗细:</span>
        <select value={lineWidth} onChange={e => setLineWidth(Number(e.target.value))}
          style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid #d9d9d9', fontSize: 13 }}>
          <option value={1}>细</option>
          <option value={2}>中</option>
          <option value={4}>粗</option>
          <option value={6}>特粗</option>
        </select>

        <div style={{ width: 1, height: 24, background: '#e8e8e8', margin: '0 4px' }} />

        <button onClick={undo} title="撤销" style={{
          padding: '5px 10px', border: '1px solid #d9d9d9', borderRadius: 4,
          background: '#fff', cursor: 'pointer', fontSize: 13
        }}>↩ 撤销</button>
        <button onClick={clearAll} title="清空" style={{
          padding: '5px 10px', border: '1px solid #d9d9d9', borderRadius: 4,
          background: '#fff', cursor: 'pointer', fontSize: 13, color: '#e74c3c'
        }}>🗑 清空</button>

        <div style={{ width: 1, height: 24, background: '#e8e8e8', margin: '0 4px' }} />

        <button onClick={() => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const link = document.createElement('a');
          link.download = `whiteboard_${title || 'untitled'}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }} style={{
          padding: '5px 10px', border: '1px solid #d9d9d9', borderRadius: 4,
          background: '#fff', cursor: 'pointer', fontSize: 13
        }}>📷 导出图片</button>

        <button onClick={() => {
          const el = containerRef.current;
          if (!el) return;
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            el.requestFullscreen?.();
          }
        }} style={{
          padding: '5px 10px', border: '1px solid #d9d9d9', borderRadius: 4,
          background: '#fff', cursor: 'pointer', fontSize: 13
        }}>⛶ 全屏</button>
      </div>

      {/* 画布 */}
      <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', height: '100%', cursor: tool === 'text' ? 'text' : 'crosshair', touchAction: 'none' }}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        />

        {/* 文字输入弹窗 */}
        {textInput.show && (
          <div style={{
            position: 'absolute', left: textInput.x * (canvasRef.current?.clientWidth || 1) / VIRTUAL_W,
            top: textInput.y * (canvasRef.current?.clientHeight || 1) / VIRTUAL_H - 30, zIndex: 10
          }}>
            <input
              autoFocus
              value={textInput.value}
              onChange={e => setTextInput({ ...textInput, value: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') submitText(); if (e.key === 'Escape') setTextInput({ show: false, x: 0, y: 0, value: '' }); }}
              onBlur={() => { if (textInput.value.trim()) submitText(); else setTextInput({ show: false, x: 0, y: 0, value: '' }); }}
              placeholder="输入文字..."
              style={{
                padding: '6px 10px', border: '2px solid #1890ff', borderRadius: 4,
                fontSize: 20, outline: 'none', minWidth: 120, background: '#fff'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}


// 获取类型标签
const getTypeLabel = (type) => {
  if (type === 'meeting') return '📋 会议纪要';
  if (type === 'research') return '📊 需求调研';
  if (type === 'whiteboard') return '🎨 白板';
  return '📝 日常记录';
};

// ===== 登录页 =====
function Login({ onLogin }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [loginType, setLoginType] = useState('dynamic'); // dynamic=动态密码, static=静态密码

  // 忘记密码弹窗
  const [showReset, setShowReset] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const sendCode = async () => {
    if (!username) return setMessage('请输入账号');
    setSending(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, type: loginType })
      });
      const data = await res.json();
      if (data.success) setMessage(loginType === 'register' ? data.message : (data.message + ' 或查看 debug_code'));
      else setMessage(data.error || '发送失败');
    } catch (e) { setMessage('网络错误'); }
    setSending(false);
  };

  // 发送重置密码验证码
  const sendResetCode = async () => {
    if (!resetUsername) return setResetMessage('请输入账号');
    setResetSending(true);
    setResetMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetUsername, type: 'reset' })
      });
      const data = await res.json();
      if (data.success) setResetMessage('验证码已发送到飞书');
      else setResetMessage(data.error || '发送失败');
    } catch (e) { setResetMessage('网络错误'); }
    setResetSending(false);
  };

  // 重置密码
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetUsername || !resetCode || !resetPassword) return setResetMessage('请填写完整信息');
    if (resetPassword.length < 4) return setResetMessage('密码至少4个字符');
    setResetLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetUsername, code: resetCode, newPassword: resetPassword })
      });
      const data = await res.json();
      if (data.success) {
        setResetMessage('密码重置成功！');
        setTimeout(() => {
          setShowReset(false);
          setResetUsername('');
          setResetCode('');
          setResetPassword('');
          setResetMessage('');
        }, 1500);
      } else {
        setResetMessage(data.error || '重置失败');
      }
    } catch (e) { setResetMessage('网络错误'); }
    setResetLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) return setMessage('请输入账号和密码');
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, loginType })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('notes_token', data.token);
        localStorage.setItem('notes_user', data.username);
        localStorage.setItem('notes_realname', data.realname || data.username);
        onLogin(data.username);
        navigate('/');
      } else {
        setMessage(data.error || '登录失败');
      }
    } catch (e) { setMessage('网络错误'); }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <h2 className="login-title">📚 知识管理</h2>
        <div className="login-tabs">
          <button className={loginType === 'dynamic' ? 'login-tab active' : 'login-tab'} onClick={() => setLoginType('dynamic')}>动态密码</button>
          <button className={loginType === 'static' ? 'login-tab active' : 'login-tab'} onClick={() => setLoginType('static')}>静态密码</button>
        </div>
        <form onSubmit={handleLogin}>
          <input type="text" placeholder="请输入账号" value={username} onChange={e => setUsername(e.target.value)} className="login-input" />
          <div className="password-row">
            <input type={loginType === 'dynamic' ? 'text' : 'password'} placeholder={loginType === 'dynamic' ? '请输入6位验证码' : '请输入密码'} value={password} onChange={e => setPassword(e.target.value)} className="login-input password-input" maxLength={loginType === 'dynamic' ? 6 : 50} />
            {loginType === 'dynamic' && (
              <button type="button" onClick={sendCode} disabled={sending} className="btn-send-code">{sending ? '...' : '获取密码'}</button>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-login">{loading ? '登录中...' : '登录'}</button>
        </form>
        {message && <p className="login-message">{message}</p>}
        {loginType === 'dynamic' && <p className="login-tip">💡 点击"获取密码"后，验证码将发送到飞书</p>}
        {loginType === 'static' && <p className="login-tip">💡 使用注册时设置的静态密码登录</p>}
        <p className="register-link">还没有账号？<span onClick={() => navigate('/register')}>立即注册</span></p>
        <p className="register-link" style={{ marginTop: 6 }}><span onClick={() => setShowReset(true)}>忘记密码？</span></p>
      </div>

      {/* 忘记密码弹窗 */}
      {showReset && (
        <div className="modal-overlay" onClick={() => setShowReset(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">🔑 重置密码</h3>
            <p className="modal-desc">输入账号后获取验证码，验证通过后设置新密码</p>
            <form onSubmit={handleResetPassword}>
              <div className="password-row">
                <input type="text" placeholder="请输入账号" value={resetUsername} onChange={e => setResetUsername(e.target.value)} className="login-input" />
                <button type="button" onClick={sendResetCode} disabled={resetSending || !resetUsername} className="btn-send-code">{resetSending ? '...' : '获取验证码'}</button>
              </div>
              <input type="text" placeholder="请输入验证码" value={resetCode} onChange={e => setResetCode(e.target.value)} className="login-input" maxLength={6} />
              <input type="password" placeholder="请输入新密码（至少4位）" value={resetPassword} onChange={e => setResetPassword(e.target.value)} className="login-input" />
              <button type="submit" disabled={resetLoading} className="btn-login" style={{ marginTop: 12 }}>{resetLoading ? '提交中...' : '重置密码'}</button>
            </form>
            {resetMessage && <p className={resetMessage.includes('成功') ? 'login-message success' : 'login-message'}>{resetMessage}</p>}
            <button onClick={() => setShowReset(false)} className="btn-modal-close">取消</button>
          </div>
        </div>
      )}
      {/* 公安部备案 */}
      <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center' }}>
        <a href="https://beian.mps.gov.cn/#/query/webSearch?code=11011502039909" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.6)', fontSize: 12, textDecoration: 'none' }}>
          <img src="/beian.png" alt="" style={{ height: 14 }} />
          京公网安备11011502039909号
        </a>
      </div>
    </div>
  );
}

// ===== 模板选择页 =====
function NewNote({ onLogout }) {
  const navigate = useNavigate();
  const handleSelectTemplate = (templateKey) => navigate(`/new?template=${templateKey}`);
  return (
    <div className="full-page">
      <div className="new-note-page">
        <div className="page-header">
          <button onClick={() => navigate('/')} className="btn-back">← 返回</button>
          <h2>选择笔记类型</h2>
          <button onClick={onLogout} className="btn-logout-small">退出</button>
        </div>
        <div className="template-grid">
          {Object.entries(TEMPLATES).map(([key, tmpl]) => (
            <div key={key} className="template-card" onClick={() => handleSelectTemplate(key)}>
              <div className="template-icon">{tmpl.name.split(' ')[0]}</div>
              <div className="template-name">{tmpl.name.split(' ')[1]}</div>
              <div className="template-desc">
                {key === 'meeting' && '包含议题、内容、决策事项、待办事项结构'}
                {key === 'research' && '需求明细表，含需求描述、提出人、现场答复'}
                {key === 'daily' && '空白文档，自由书写'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===== 笔记列表 =====
function NoteList({ onLogout }) {
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState(''); // 筛选类型
  const [loading, setLoading] = useState(true);
  const currentUser = getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => { fetchNotes(); }, [search]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const url = search ? `${API_URL}/api/notes?search=${encodeURIComponent(search)}` : `${API_URL}/api/notes`;
      const res = await fetchWithAuth(url);
      setNotes(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // 过滤后的笔记
  const filteredNotes = filterType ? notes.filter(note => {
    try {
      const data = JSON.parse(note.content);
      return data.type === filterType;
    } catch (e) { return filterType === 'daily'; }
  }) : notes;

  const deleteNote = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const note = notes.find(n => n.id === id);
    const data = note?.content ? JSON.parse(note.content) : null;
    if (data?.created_by && data.created_by !== currentUser) {
      alert('您只能删除自己创建的笔记');
      return;
    }
    if (!confirm('确定删除这条笔记？')) return;
    try { await fetchWithAuth(`${API_URL}/api/notes/${id}`, { method: 'DELETE' }); fetchNotes(); }
    catch (e) { alert('删除失败'); }
  };

  return (
    <div className="notes-list-page">
      <div className="notes-page">
        <div className="page-header">
          <h1>📝 我的笔记</h1>
          <div className="page-actions">
            <input type="text" placeholder="搜索知识库..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
            <button onClick={() => navigate('/new-note')} className="btn-primary">+ 新建</button>
          </div>
        </div>
        {loading ? <div className="loading">加载中...</div> : notes.length === 0 ? (
          <div className="empty-state"><p>还没有笔记</p><button onClick={() => navigate('/new-note')} className="btn-primary">创建第一篇</button></div>
        ) : (
          <>
            {/* 类型筛选按钮 */}
            <div className="filter-buttons">
              <button 
                className={filterType === '' ? 'btn-filter active' : 'btn-filter'} 
                onClick={() => setFilterType('')}
              >全部</button>
              <button 
                className={filterType === 'meeting' ? 'btn-filter active' : 'btn-filter'} 
                onClick={() => setFilterType('meeting')}
              >📋 会议纪要</button>
              <button 
                className={filterType === 'research' ? 'btn-filter active' : 'btn-filter'} 
                onClick={() => setFilterType('research')}
              >📊 需求调研</button>
              <button 
                className={filterType === 'daily' ? 'btn-filter active' : 'btn-filter'} 
                onClick={() => setFilterType('daily')}
              >📝 日常记录</button>
              <button 
                className={filterType === 'whiteboard' ? 'btn-filter active' : 'btn-filter'} 
                onClick={() => setFilterType('whiteboard')}
              >🎨 白板</button>
            </div>
            <div className="note-grid">
            {filteredNotes.map(note => {
              let noteType = '📝 日常记录';
              let previewText = '暂无内容';
              
              try {
                const data = JSON.parse(note.content);
                if (data.type === 'meeting') {
                  noteType = '📋 会议纪要';
                  // 找会议主题
                  const themeSection = data.sections?.find(s => s.label === '会议主题');
                  previewText = themeSection?.value ? `会议主题：${themeSection.value}` : '会议纪要';
                } else if (data.type === 'research') {
                  noteType = '📊 需求调研';
                  // 找调研主题
                  const themeSection = data.sections?.find(s => s.label === '调研主题');
                  previewText = themeSection?.value ? `调研主题：${themeSection.value}` : '需求调研';
                } else if (data.type === 'daily') {
                  noteType = '📝 日常记录';
                  const stripped = (data.content || '').replace(/<[^>]*>/g, '');
                  previewText = stripped.substring(0, 100) || '日常记录';
                } else if (data.type === 'whiteboard') {
                  noteType = '🎨 白板';
                  previewText = note.title || ((data.elements || []).length > 0 ? `白板（${(data.elements || []).length} 个绘制元素）` : '白板（空白）');
                }
              } catch (e) {
                // 不是JSON，直接显示内容
                previewText = note.content?.substring(0, 100) || '暂无内容';
              }
              
              return (
                <div key={note.id} className="note-card" onClick={() => navigate(`/note/${note.id}`)}>
                  <div className="note-type-badge">{noteType}</div>
                  <p className="note-preview">{previewText}</p>
                  <div className="note-meta">
                    <span>{format(new Date(note.updated_at), 'yyyy-MM-dd HH:mm')}</span>
                    {note.created_by_name && <span style={{marginLeft: 8, color: '#999', fontSize: 12}}>创建人: {note.created_by_name}</span>}
                    <button onClick={e => deleteNote(e, note.id)} className="btn-delete">删除</button>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

// ===== 表格组件 =====
function EditableTable({ columns, rows, onChange, label, showAutoNo, readOnly, columnWidths }) {
  const addRow = () => {
    const newNo = rows.length + 1;
    const newCols = columns.map((c, i) => showAutoNo && i === 0 ? String(newNo) : '');
    onChange([...rows, { cols: newCols }]);
  };
  const deleteRow = (idx) => {
    if (rows.length <= 1) return;
    const newRows = rows.filter((_, i) => i !== idx).map((row, i) => ({
      cols: row.cols.map((c, j) => showAutoNo && j === 0 ? String(i + 1) : c)
    }));
    onChange(newRows);
  };
  const updateCell = (rowIdx, colIdx, value) => {
    if (readOnly) return;
    const newRows = rows.map((row, i) => {
      if (i !== rowIdx) return row;
      return { cols: row.cols.map((c, j) => j === colIdx ? value : c) };
    });
    onChange(newRows);
  };

  // 计算列宽样式
  const getColStyle = (idx) => {
    if (!columnWidths || !columnWidths[idx]) return {};
    const width = columnWidths[idx];
    return width.toString().includes('fr') 
      ? { width: 'auto', flex: width }
      : { width: width };
  };
  
  return (
    <div className={`editable-table ${readOnly ? 'readonly' : ''}`}>
      <div className="table-label">{label}</div>
      <table className="custom-width-table">
        <colgroup>
          {columns.map((col, i) => <col key={i} style={getColStyle(i)} />)}
          <col style={{ width: '60px' }} />
        </colgroup>
        <thead>
          <tr>{columns.map((col, i) => <th key={i}>{col}</th>)}<th>操作</th></tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {row.cols.map((cell, colIdx) => (
                <td key={colIdx}>
                  <textarea 
                    value={cell} 
                    onChange={e => updateCell(rowIdx, colIdx, e.target.value)} 
                    readOnly={showAutoNo && colIdx === 0 || readOnly}
                    className={showAutoNo && colIdx === 0 ? 'cell-auto-no' : ''}
                    rows={2}
                  />
                </td>
              ))}
              <td>{!readOnly && <button onClick={() => deleteRow(rowIdx)} className="btn-delete-row" title="删除行">🗑</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && <button onClick={addRow} className="btn-add-row">+ 添加行</button>}
    </div>
  );
}

// ===== 笔记编辑器 =====
function NoteEditor({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const currentUser = getCurrentUser();
  const urlParams = new URLSearchParams(window.location.search);
  const templateKey = urlParams.get('template');

  const [title, setTitle] = useState('');
  const [noteData, setNoteData] = useState(null);
  const [rawContent, setRawContent] = useState('');
  const [mode, setMode] = useState(isNew ? 'edit' : 'preview');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [isOwner, setIsOwner] = useState(true);
  const [owner, setOwner] = useState('');
  const [saveStatus, setSaveStatus] = useState(''); // 保存状态
  const [showSmartImport, setShowSmartImport] = useState(false); // 智能导入弹窗
  const saveTimeoutRef = useRef(null);
  const editorRef = useRef(null);
  const colorInputRef = useRef(null);

  useEffect(() => {
    if (!isNew) fetchNote();
    else if (templateKey && TEMPLATES[templateKey]) {
      const tmpl = TEMPLATES[templateKey];
      const data = tmpl.getContent();
      setTitle(data.title);
      setNoteData(data);
    }
  }, [id, templateKey]);

  const fetchNote = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/notes/${id}`);
      if (!res.ok) throw new Error('笔记不存在');
      const data = await res.json();
      setTitle(data.title);
      setRawContent(data.content || '');
      setAttachments(data.attachments || []);
      const parsed = parseContentToData(data.content);
      setNoteData(parsed);
      // 检查所有者
      if (parsed && parsed.created_by) {
        setOwner(parsed.created_by);
        setIsOwner(parsed.created_by === currentUser);
      }
    } catch (e) { alert('笔记不存在'); navigate('/'); }
    setLoading(false);
  };

  const generateContent = (data) => {
    if (!data) return rawContent;
    
    // 所有类型都保存为 JSON，包含创建者信息
    const contentData = { 
      ...data, 
      created_by: isNew ? currentUser : (owner || currentUser) 
    };
    return JSON.stringify(contentData);
  };

  // 解析内容
  const parseContentToData = (content) => {
    if (!content) return { type: 'daily', content: '' };
    try {
      const data = JSON.parse(content);
      // 有sections的是结构化模板
      if (data && data.sections) return data;
      // 有content字段的是日常记录
      if (data && data.type === 'daily') {
        return data;
      }
      // 白板类型
      if (data && data.type === 'whiteboard' && data.elements) {
        return data;
      }
      // 其他情况返回默认值
      return { type: 'daily', content: '' };
    } catch (e) {
      // JSON解析失败，返回空日常记录
      return { type: 'daily', content: '' };
    }
  };

  const updateSection = (sectionIdx, field, value) => {
    if (!isOwner) return;
    setSaveStatus('有修改...');
    const newData = { ...noteData };
    newData.sections[sectionIdx] = { ...newData.sections[sectionIdx], [field]: value };
    setNoteData(newData);
  };

  const updateTable = (sectionIdx, rows) => {
    if (!isOwner) return;
    setSaveStatus('有修改...');
    const newData = { ...noteData };
    newData.sections[sectionIdx] = { ...newData.sections[sectionIdx], rows };
    setNoteData(newData);
  };

  const saveNote = async (silent = false) => {
    if (!isOwner && !isNew) {
      if (!silent) alert('您只能修改自己创建的笔记');
      return;
    }
    // 先同步富文本编辑区的内容
    if (editorRef.current && editorRef.current._draftHTML !== undefined) {
      setNoteData(prev => ({ ...prev, content: editorRef.current._draftHTML }));
      editorRef.current._draftHTML = undefined;
    }
    // 确保 noteData 是最新的
    const currentNoteData = noteData;
    const content = generateContent(currentNoteData);
    try {
      setSaveStatus('保存中...');
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew ? `${API_URL}/api/notes` : `${API_URL}/api/notes/${id}`;
      const res = await fetchWithAuth(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title, content }) });
      const data = await res.json();
      setSaveStatus('已保存');
      if (isNew) navigate(`/note/${data.id}`, { replace: true });
      else if (!silent) alert('保存成功');
      // 3秒后清除保存状态
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (e) { 
      setSaveStatus('保存失败');
      if (!silent) alert('保存失败'); 
    }
  };

  // 自动保存 - 当noteData变化时，3秒后自动保存
  useEffect(() => {
    if (!isOwner && !isNew) return; // 非创建者不自动保存
    if (!noteData) return; // 数据未加载不保存
    
    // 清除之前的定时器
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // 设置新的定时器，3秒后自动保存
    saveTimeoutRef.current = setTimeout(() => {
      saveNote(true); // true表示静默保存
    }, 3000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [noteData, title]);

  const onDrop = async (acceptedFiles) => {
    if (isNew) { alert('请先保存笔记再上传附件'); return; }
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetchWithAuth(`${API_URL}/api/notes/${id}/attachments`, { method: 'POST', body: formData });
        const data = await res.json();
        setAttachments(prev => [...prev, data]);
      } catch (e) { alert('上传失败: ' + file.name); }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: true });

  const deleteAttachment = async (attachmentId) => {
    if (!isOwner) { alert('您只能删除自己笔记的附件'); return; }
    if (!confirm('确定删除此附件？')) return;
    try { await fetch(`${API_URL}/api/attachments/${attachmentId}`, { method: 'DELETE' }); setAttachments(prev => prev.filter(a => a.id !== attachmentId)); }
    catch (e) { alert('删除失败'); }
  };

  const isImage = (mimeType) => mimeType?.startsWith('image/');

  if (loading) return <div className="loading">加载中...</div>;

  const renderPreviewHTML = (noteTitle) => {
    const data = noteData || { type: 'daily', content: '' };
    
    // 白板 - 显示静态占位
    if (data.type === 'whiteboard') {
      const titleText = noteTitle || '';
      const elCount = (data.elements || []).length;
      let displayText = '🎨 白板';
      if (titleText) displayText += `：${titleText}`;
      const elText = elCount > 0 ? `（${elCount} 个绘制元素）` : '（空白画板）';
      return `<div class="preview-text" style="text-align: center; padding: 60px 20px; color: #999; font-size: 16px;">
        ${displayText} ${elText}<br/>
        <span style="font-size: 13px; color: #bbb;">切换到编辑模式查看和修改</span>
      </div>`;
    }
    
    // 日常记录 - 直接显示内容
    if (data.type === 'daily') {
      return `<div class="preview-text" style="white-space: pre-wrap; padding: 20px;">${data.content || '—'}</div>`;
    }
    
    // 结构化模板
    let html = '';
    data.sections.forEach(section => {
      if (section.type === 'header') {
        html += `<div class="preview-header"><span class="preview-label">${section.label}：</span><span class="preview-value">${section.value || '—'}</span></div>`;
      } else if (section.type === 'text') {
        html += `<div class="preview-section"><div class="preview-section-title">${section.label}</div><div class="preview-text">${section.value || '—'}</div></div>`;
      } else if (section.type === 'table') {
        html += `<div class="preview-section"><div class="preview-section-title">${section.label}</div>`;
        html += `<table class="preview-table">`;
        // 设置列宽
        const colWidths = section.columnWidths || [];
        const hasSeq = section.columns[0] === '序号';
        const widths = colWidths.length > 0 ? colWidths : (hasSeq ? ['28px'] : []);
        if (widths.length > 0) {
          html += '<colgroup>';
          widths.forEach(w => html += `<col style="width:${w}">`);
          html += '</colgroup>';
        }
        html += `<thead><tr>`;
        section.columns.forEach(col => html += `<th>${col}</th>`);
        html += `</tr></thead><tbody>`;
        section.rows.forEach((row, idx) => {
          html += `<tr>`;
          row.cols.forEach((cell, colIdx) => {
            const cellValue = (section.columns[0] === '序号' && colIdx === 0) ? String(idx + 1) : cell;
            html += `<td>${cellValue || '—'}</td>`;
          });
          html += `</tr>`;
        });
        html += `</tbody></table></div>`;
      }
    });
    return html;
  };

  return (
    <div className="full-page">
      <div className="editor-page">
        <div className="page-header sticky">
          <div className="header-left">
            <button onClick={() => navigate('/')} className="btn-back">← 返回</button>
            <button onClick={onLogout} className="btn-logout-small">退出</button>
          </div>
          <div className="header-right">
            {saveStatus && <span className="save-status">{saveStatus}</span>}
            {!isNew && noteData?.type !== 'whiteboard' && (
              <>
                <button onClick={() => window.open(`${API_URL}/api/notes/${id}/export/word?token=${getToken()}`, '_blank')} className="btn-export">📄 导出Word</button>
                <button onClick={() => window.open(`${API_URL}/api/notes/${id}/export/pdf?token=${getToken()}`, '_blank')} className="btn-export">📕 导出PDF</button>
              </>
            )}
            <button onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')} className="btn-mode">{mode === 'edit' ? '返回预览' : '修改'}</button>
            {isOwner && <button onClick={() => saveNote(false)} className="btn-primary">保存</button>}
          </div>
        </div>

        {!isOwner && <div className="owner-notice">此笔记由 {owner} 创建，您只能查看</div>}

        <div className="editor-container">
          {/* 模板类型标签 */}
          {noteData && noteData.type && (
            <div className="template-badge">{getTypeLabel(noteData.type)}</div>
          )}

          {/* 智能导入按钮 - 仅会议纪要和需求调研模板显示 */}
          {mode === 'edit' && noteData && (noteData.type === 'meeting' || noteData.type === 'research') && (
            <div className="smart-import-bar">
              <button 
                className="btn-smart-import" 
                onClick={() => setShowSmartImport(true)}
                title="从文字智能导入内容"
              >
                🤖 智能导入
              </button>
              <span className="smart-import-hint">从逐字稿、纪要或笔记自动提取内容</span>
            </div>
          )}

          {mode === 'edit' ? (
            <div className="edit-mode">
              {noteData && noteData.type === 'whiteboard' ? (
                // 白板 - 轻量 Canvas 画布
                <div style={{ height: 'calc(100vh - 250px)' }}>
                  <WhiteboardEditor 
                    elements={noteData.elements || []}
                    appState={noteData.appState || { viewBackgroundColor: '#ffffff' }}
                    title={noteData?.title || title}
                    onTitleChange={(newTitle) => {
                      if (!isOwner) return;
                      setTitle(newTitle);
                      setSaveStatus('有修改...');
                    }}
                    onChange={(elements, appState) => {
                      if (!isOwner) return;
                      setSaveStatus('有修改...');
                      setNoteData({ ...noteData, elements, appState });
                    }}
                  />
                </div>
              ) : noteData && noteData.type === 'daily' ? (
                // 日常记录 - 富文本编辑
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px', background: '#f8f8f8', borderBottom: '1px solid #e8e8e8', flexWrap: 'wrap' }}>
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('bold'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14 }} title="加粗">B</button>
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('italic'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', fontStyle: 'italic', fontSize: 14 }} title="斜体"><i>I</i></button>
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('strikeThrough'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', textDecoration: 'line-through', fontSize: 14 }} title="删除线">S</button>
                    <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('formatBlock', false, 'h2'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', fontSize: 14 }} title="标题">H2</button>
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('formatBlock', false, 'h3'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', fontSize: 13 }} title="副标题">H3</button>
                    <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('insertUnorderedList'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', fontSize: 14 }} title="无序列表">≡</button>
                    <button onMouseDown={e => { e.preventDefault(); document.execCommand('insertOrderedList'); editorRef.current?.focus(); }} style={{ padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 3, background: '#fff', cursor: 'pointer', fontSize: 14 }} title="有序列表">#</button>
                    <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />
                    <input type="color" defaultValue="#e74c3c" ref={colorInputRef} onMouseDown={() => {
                      // 保存当前选区
                      const sel = window.getSelection();
                      if (sel && sel.rangeCount > 0) {
                        colorInputRef.current._savedRange = sel.getRangeAt(0).cloneRange();
                      }
                    }} onChange={e => {
                      const val = e.target.value;
                      const saved = colorInputRef.current?._savedRange;
                      if (saved) {
                        const sel = window.getSelection();
                        sel.removeAllRanges();
                        sel.addRange(saved);
                      }
                      document.execCommand('foreColor', false, val);
                      editorRef.current?.focus();
                    }} style={{ width: 24, height: 24, padding: 0, border: 'none', cursor: 'pointer', verticalAlign: 'middle' }} title="字体颜色" />
                    <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />
                    <style>{`.rich-toolbar-btn:active,.rich-toolbar-btn:hover{background:#e6f7ff}`}</style>
                  </div>
                  <div
                    ref={editorRef}
                    className="content-textarea"
                    contentEditable
                    suppressContentEditableWarning
                    style={{ flex: 1, padding: '16px 20px', outline: 'none', overflowY: 'auto', lineHeight: 1.8, minHeight: 300, fontSize: 15 }}
                    dangerouslySetInnerHTML={{ __html: noteData.content || '' }}
                    onInput={e => {
                      if (!isOwner) return;
                      // 只更新 ref，不更新 React state，避免光标跳回开头
                      editorRef.current._draftHTML = e.target.innerHTML;
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Tab') { e.preventDefault(); document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;'); }
                    }}
                    onBlur={() => {
                      // 离开编辑时保存到 noteData
                      if (editorRef.current?._draftHTML !== undefined) {
                        setNoteData({ ...noteData, content: editorRef.current._draftHTML });
                        editorRef.current._draftHTML = undefined;
                      }
                    }}
                  />
                </div>
              ) : noteData && noteData.sections && noteData.sections.length > 0 ? (
                // 结构化模板
                noteData.sections.map((section, sectionIdx) => (
                <div key={sectionIdx} className="section-block">
                  {section.type === 'header' && (
                    <div className="field-row">
                      <label>{section.label}：</label>
                      {['会议时间', '时间', '调研时间'].includes(section.label) ? (
                        <input type="date" value={section.value && section.value.match(/\d{4}-\d{2}-\d{2}/) ? section.value.match(/\d{4}-\d{2}-\d{2}/)[0] : ''} onChange={e => updateSection(sectionIdx, 'value', e.target.value)} readOnly={!isOwner} className={!isOwner ? 'readonly' : ''} />
                      ) : (
                        <input type="text" value={section.value} onChange={e => updateSection(sectionIdx, 'value', e.target.value)} placeholder={`请输入${section.label}`} readOnly={!isOwner} className={!isOwner ? 'readonly' : ''} />
                      )}
                    </div>
                  )}
                  {section.type === 'text' && (
                    <div className="field-block">
                      <label>{section.label}</label>
                      <textarea value={section.value} onChange={e => updateSection(sectionIdx, 'value', e.target.value)} placeholder={`请输入${section.label}`} readOnly={!isOwner} className={!isOwner ? 'readonly' : ''} />
                    </div>
                  )}
                  {section.type === 'table' && (
                    <EditableTable 
                      columns={section.columns} 
                      rows={section.rows} 
                      label={section.label} 
                      onChange={rows => updateTable(sectionIdx, rows)}
                      showAutoNo={section.columns[0] === '序号'}
                      readOnly={!isOwner}
                      columnWidths={section.columnWidths}
                    />
                  )}
                </div>
              ))) : (
                <textarea className="content-textarea" value={noteData?.content || ''} onChange={e => { if (!isOwner) return; setSaveStatus('有修改...'); setNoteData({ ...noteData, content: e.target.value }); }} placeholder="开始写笔记..." readOnly={!isOwner} />
              )}
            </div>
          ) : (
            <div className="preview-mode" dangerouslySetInnerHTML={{ __html: renderPreviewHTML(title) }} />
          )}

          <div {...getRootProps()} className="dropzone">
            <input {...getInputProps()} />
            <p>📎 点击或拖拽上传附件/图片到这里</p>
          </div>

          {attachments.length > 0 && (
            <div className="attachments">
              <h4>附件 ({attachments.length})</h4>
              <div className="attachment-list">
                {attachments.map(att => (
                  <div key={att.id} className="attachment-item">
                    {isImage(att.mime_type) ? (
                      <div className="attachment-image"><img src={`${API_URL}${att.url}`} alt={att.original_name} /><span>{att.original_name}</span></div>
                    ) : (
                      <a href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer">📄 {att.original_name}</a>
                    )}
                    {isOwner && <button onClick={() => deleteAttachment(att.id)} className="btn-delete-small">×</button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 智能导入弹窗 */}
          <SmartImportModal
            isOpen={showSmartImport}
            onClose={() => setShowSmartImport(false)}
            onImport={(data) => {
              if (data.title) setTitle(data.title);
              if (data.sections && noteData) {
                const newNoteData = { ...noteData, sections: data.sections };
                // 保留原始的 created_by
                if (noteData.created_by) newNoteData.created_by = noteData.created_by;
                setNoteData(newNoteData);
                setSaveStatus('已智能导入');
                setTimeout(() => setSaveStatus(''), 3000);
              }
            }}
            templateType={noteData?.type || 'meeting'}
          />
        </div>
      </div>
    </div>
  );
}

// ===== 智能导入弹窗组件 =====
function SmartImportModal({ isOpen, onClose, onImport, templateType }) {
  const [mode, setMode] = useState('verbatim');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const modeOptions = [
    { key: 'verbatim', label: '🎙️ 会议逐字稿', desc: '从录音转写文本中提取要点' },
    { key: 'meeting-summary', label: '📝 会议纪要（软件生成）', desc: '将会议软件的纪要转换为标准格式' },
    { key: 'manual-notes', label: '✍️ 简易笔记', desc: '将人工记录的笔记扩写为完整纪要' }
  ];

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const ext = file.name.toLowerCase().split('.').pop();
    const validExts = ['txt', 'docx', 'doc'];
    
    if (!validExts.includes(ext)) {
      setError('请选择 .txt、.docx 或 .doc 格式文件');
      return;
    }

    // txt 直接本地读取，Word 文件上传到后端解析
    if (ext === 'txt') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setText(event.target.result);
        setError('');
      };
      reader.onerror = () => setError('文件读取失败');
      reader.readAsText(file);
    } else {
      // Word 文件上传解析
      setLoading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await fetchWithAuth(`${API_URL}/api/notes/import-file`, {
          method: 'POST',
          body: formData
        });
        
        const data = await res.json();
        if (data.success) {
          setText(data.text);
          setError('');
        } else {
          setError(data.error || '文件解析失败');
        }
      } catch (e) {
        setError('文件上传失败，请稍后重试');
      }
      
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!text.trim()) {
      setError('请输入或上传文字内容');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/notes/analyze-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, mode, templateType })
      });

      const data = await res.json();
      
      if (data.success) {
        setPreview(data.data);
      } else {
        setError(data.error || '分析失败');
      }
    } catch (e) {
      setError('网络错误，请稍后重试');
    }
    
    setLoading(false);
  };

  const handleConfirm = () => {
    if (preview) {
      onImport(preview);
      handleReset();
      onClose();
    }
  };

  const handleReset = () => {
    setMode('verbatim');
    setText('');
    setError('');
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🤖 智能导入</h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        {!preview ? (
          <div className="modal-body">
            {/* 模式选择 */}
            <div className="form-group">
              <label>选择导入模式</label>
              <div className="mode-options">
                {modeOptions.map(opt => (
                  <div 
                    key={opt.key}
                    className={`mode-option ${mode === opt.key ? 'active' : ''}`}
                    onClick={() => setMode(opt.key)}
                  >
                    <div className="mode-label">{opt.label}</div>
                    <div className="mode-desc">{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 文本输入 */}
            <div className="form-group">
              <label>粘贴文字内容</label>
              <textarea
                className="import-textarea"
                placeholder="请粘贴会议文字内容..."
                value={text}
                onChange={e => setText(e.target.value)}
                rows={10}
              />
            </div>

            {/* 文件上传 */}
            <div className="form-group">
              <label>或上传文件</label>
              <input
                type="file"
                ref={fileInputRef}
                accept=".txt,.docx,.doc"
                onChange={handleFileSelect}
                className="file-input"
              />
              <div className="file-hint">支持格式：.txt、.docx、.doc</div>
            </div>

            {error && <div className="error-message">{error}</div>}
          </div>
        ) : (
          <div className="modal-body">
            <div className="preview-section">
              <h4>📋 分析结果预览</h4>
              <div className="preview-content">
                <p><strong>主题：</strong>{preview.title}</p>
                {preview.sections?.map((sec, idx) => (
                  <div key={idx} className="preview-item">
                    {sec.type === 'header' && (
                      <p><strong>{sec.label}：</strong>{sec.value || '—'}</p>
                    )}
                    {sec.type === 'text' && sec.value && (
                      <div>
                        <p><strong>{sec.label}</strong></p>
                        <pre>{sec.value}</pre>
                      </div>
                    )}
                    {sec.type === 'table' && sec.rows?.length > 0 && sec.rows[0].cols?.some(c => c) && (
                      <div>
                        <p><strong>{sec.label}</strong>（{sec.rows.length} 条）</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {!preview ? (
            <>
              <button className="btn-secondary" onClick={handleClose}>取消</button>
              <button className="btn-primary" onClick={handleAnalyze} disabled={loading}>
                {loading ? '⏳ 分析中...' : '🤖 开始分析'}
              </button>
            </>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => setPreview(null)}>返回修改</button>
              <button className="btn-primary" onClick={handleConfirm}>✅ 确认填充</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== 布局（顶部导航 + 内容区） =====
function AppLayout({ onLogout }) {
  // Session超时检查（4小时无操作自动退出）
  useEffect(() => {
    const SESSION_TIMEOUT = 4 * 60 * 60 * 1000; // 4小时
    const CHECK_INTERVAL = 60 * 1000; // 分钟检查一次
    
    const checkSession = () => {
      const lastActivity = localStorage.getItem('notes_last_activity');
      const token = localStorage.getItem('notes_token');
      if (!token) return;
      
      if (lastActivity) {
        const elapsed = Date.now() - parseInt(lastActivity, 10);
        if (elapsed > SESSION_TIMEOUT) {
          console.log('Session expired, logging out');
          localStorage.removeItem('notes_token');
          localStorage.removeItem('notes_user');
          localStorage.removeItem('notes_last_activity');
          window.location.href = '/';
        }
      } else {
        localStorage.setItem('notes_last_activity', Date.now().toString());
      }
    };
    
    const updateActivity = () => {
      localStorage.setItem('notes_last_activity', Date.now().toString());
    };
    
    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('mousemove', updateActivity);
    
    const intervalId = setInterval(checkSession, CHECK_INTERVAL);
    checkSession();
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('mousemove', updateActivity);
    };
  }, []);

  return (
    <div className="app-layout-new">
      <TopNavbar onLogout={onLogout} />
      <main className="app-main-new">
        <Outlet />
        {/* 公安部备案 */}
        <div style={{ textAlign: 'center', padding: '24px 0 16px', borderTop: '1px solid #f0f0f0', marginTop: 24 }}>
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=11011502039909" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#bbb', fontSize: 12, textDecoration: 'none' }}>
            <img src="/beian.png" alt="" style={{ height: 16 }} />
            京公网安备11011502039909号
          </a>
        </div>
      </main>
    </div>
  );
}

// ===== 主应用 =====
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('notes_token'));
  const handleLogin = () => setIsLoggedIn(true);
  const handleLogout = () => { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); localStorage.removeItem('notes_realname'); setIsLoggedIn(false); };

  return (
    <ConfigProvider locale={zhCN}>
      <BrowserRouter>
        <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/portal" element={<PortalHome />} />
        <Route path="*" element={
          isLoggedIn ? (
            <Routes>
              <Route element={<AppLayout onLogout={handleLogout} />}>
                <Route path="/" element={<PortalHome />} />
                <Route path="/notes" element={<NoteList onLogout={handleLogout} />} />
                <Route path="/new-note" element={<NewNote onLogout={handleLogout} />} />
                <Route path="/note/:id" element={<NoteEditor onLogout={handleLogout} />} />
                <Route path="/new" element={<NoteEditor onLogout={handleLogout} />} />
                <Route path="/workorder-dashboard" element={<WorkorderDashboard />} />
                <Route path="/workorder-import" element={<WorkorderImport />} />
                <Route path="/handover" element={<Handover />} />
                <Route path="/manuals" element={<ManualsPage />} />
                <Route path="/bid-document" element={<BidDocumentBuilder />} />
                <Route path="/ism-extract" element={<ISMExtractor />} />
                <Route path="/knowledge" element={<KnowledgeManager />} />
                <Route path="/knowledge/ism" element={<ISMExtractor />} />
                <Route path="/knowledge/browse/:name" element={<KnowledgeBrowser />} />
                <Route path="/knowledge/search" element={<KnowledgeSearch />} />
                <Route path="/knowledge/qa" element={<KnowledgeQA />} />
                <Route path="/knowledge/dictionary" element={<DataDictionary />} />
                <Route path="/knowledge/log-analysis" element={<LogAnalysis />} />
                <Route path="/professional-assistant" element={<ProfessionalAssistant />} />
                <Route path="/qa-stats" element={<QAStats />} />
                <Route path="/profile" element={<Profile onLogout={handleLogout} />} />
                <Route path="/permissions" element={<PermissionManager />} />
                <Route path="/users" element={<UserManager />} />
                <Route path="/announcements" element={<AnnouncementManager />} />
                <Route path="/patch" element={<PatchManager />} />
                <Route path="/projects" element={<ProjectList />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/projects/:id/tasks/new" element={<TaskCreate />} />
                <Route path="/projects/:id/board" element={<TaskBoard />} />
                <Route path="/projects/:id/task-overview" element={<TaskOverview />} />
                <Route path="/projects/:id/completed-tasks" element={<CompletedTaskMaintenance />} />
                <Route path="/tasks/:taskId" element={<TaskMaintenance />} />
                <Route path="/monitor" element={<MonitorDashboard />} />
                <Route path="/monitor/config/:projectId" element={<MonitorConfig />} />
                <Route path="/monitor/data/:projectId" element={<MonitorDataEntry />} />
                <Route path="/monitor/viewer/:projectId" element={<MonitorDataViewer />} />
                <Route path="/monitor/report/:projectId" element={<MonitorReport />} />
                <Route path="/monitor/layout/:projectId" element={<MonitorLayoutConfig />} />
                <Route path="/diagrams" element={<DiagramList />} />
                <Route path="/diagrams/editor/:id" element={<DiagramEditor />} />
                <Route path="/law" element={<LawDashboard />} />
                <Route path="/law/cases" element={<CaseManager />} />
                <Route path="/law/logs" element={<WorkLogManager />} />
                <Route path="/law/report" element={<LegalReport />} />
                <Route path="/legal-qa" element={<LegalQA />} />
                <Route path="/work-logs" element={<WorkLogHome />} />
                <Route path="/work-logs/entry" element={<WorkLogEntry />} />
                <Route path="/work-logs/groups" element={<WorkGroupManager />} />
                <Route path="/work-logs/overview" element={<WorkLogOverview />} />
                <Route path="/work-logs/report" element={<WorkLogReport />} />
                <Route path="/work-logs/import" element={<WorkLogImport />} />
                <Route path="/work-logs/attachments" element={<WorkLogAttachments />} />
                <Route path="/work-logs/doc-projects" element={<ProjectDocHome />} />
                <Route path="/work-logs/doc-project/:projectId" element={<ProjectDocDetail />} />
                <Route path="/work-logs/doc-overview" element={<ProjectDocOverview />} />
              </Route>
            </Routes>
          ) : <Login onLogin={handleLogin} />
        } />
      </Routes>
    </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;